import type { RefObject } from 'preact';
import { useState, useRef, useCallback, useEffect } from 'preact/hooks';
import { classNames } from '#utils/UNSAFE_classNames';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { createDragImage } from '#utils/PRIVATE_dndUtils';
import { getKey, getItemKeysArray } from '#utils/PRIVATE_collectionUtils';
import { useUser } from '#hooks/UNSAFE_useUser';
import { ReorderableItemProps } from '#UNSAFE_Collection/Reorderable.types';
import { styles } from './themes/ReorderableItemStyles.css';
import { isKeyDefined } from '#utils/UNSAFE_keys';

export type useReorderableOptions<K extends string | number> = Omit<
  ReorderableItemProps<K>,
  'children'
> & {
  isDisabled: boolean;
  ref: RefObject<HTMLDivElement>;
  getItem: () => Element | null | undefined; // a function to get the reorderable item
  setTransferData: (event: DragEvent) => void; // a function to set the transfer data and type
  itemStyles: {
    // component specific style for two overlay divs, e.g. component has different gap sizes
    itemOverlayFront: string;
    itemOverlayRear: string;
    itemDragger?: string; // component specific style for drag image
  };
  itemSelector: string;
  isVertical?: boolean; // used when the component is vertical and has one column, e.g. ListView
};

/**
 * Implements mouse reorder behavior for list-like components, e.g. TabBar, ListView, CardView
 */
export function useReorderableItem<K extends string | number>(options: useReorderableOptions<K>) {
  const {
    isDisabled,
    dragKey,
    setDragKey,
    onReorder,
    ref,
    rootRef,
    itemStyles,
    getItem,
    setTransferData,
    itemSelector,
    isVertical
  } = options;

  const [itemKey, setItemKey] = useState<K | undefined>(undefined);

  // This is used to set the current itemKey and that in turn sets isDragged class. It needs to be updated everytime dragKey changes because otherwise itemKey will be the previously draggedKey
  useEffect(() => {
    if (ref.current) {
      const item = getItem();
      const key = item != null ? (getKey(item as HTMLElement) as K) : undefined;
      setItemKey(key);
    }
  }, [dragKey]);

  const [position, setPosition] = useState<'before' | 'after' | undefined>();
  const pendingPositionRef = useRef<typeof position>();
  const dragImgRef = useRef<HTMLElement | undefined>(undefined);

  const itemDraggerClasses = classNames([styles.itemDragger, itemStyles.itemDragger]);

  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  /**
   * Drag start handler
   * The data-oj-draggable attribute need to be required and set to true to the target element. This
   * attribute is used to identify which element can act as drag handles during the drag and drop process.
   */
  const handleDragStart = useCallback(
    (e: DragEvent) => {
      const item = getItem();
      let targetElement: HTMLElement | null = e.target as HTMLElement;
      const currentDragKey = item != null ? (getKey(item as HTMLElement) as K) : undefined;

      let dragHandle;
      while (targetElement) {
        //data-oj-draggable attribute is only to use to draggable element
        if (targetElement.hasAttribute('data-oj-draggable')) {
          dragHandle = targetElement;
          break;
        } else if (targetElement === item) {
          break;
        }
        targetElement = targetElement.parentElement;
      }

      if (!dragHandle) {
        e.preventDefault();
        e.stopPropagation();
      } else {
        if (ref?.current && setDragKey) {
          dragImgRef.current = createDragImage(ref.current, itemDraggerClasses);
          if (ref.current !== dragHandle) {
            e?.dataTransfer?.setDragImage(
              dragImgRef.current,
              dragHandle.offsetLeft + dragHandle.offsetWidth / 2,
              dragHandle.offsetTop + dragHandle.offsetHeight / 2
            );
          } else {
            e?.dataTransfer?.setDragImage(
              dragImgRef.current,
              dragHandle.offsetWidth / 2,
              dragHandle.offsetHeight / 2
            );
          }
          setTransferData(e);
          setDragKey(currentDragKey as K);
        }
      }
    },
    [getItem, setTransferData, itemDraggerClasses, ref, setDragKey]
  );

  const handleDragOver = useCallback(
    (e: DragEvent) => {
      if (isKeyDefined(dragKey)) {
        e.preventDefault();
      }
    },
    [dragKey]
  );

  //Once drag is over, reset styling of dragged item to it's original styling
  const handleDragEnd = useCallback(() => {
    setDragKey?.(undefined);
    document.body.removeChild(dragImgRef.current as HTMLElement);
  }, [setDragKey]);

  // detect drop target position on the overlay divs and render drop target
  const handleDragEnterBeforeZone = () => {
    setPosition('before');
    pendingPositionRef.current = 'before';
  };

  const handleDragLeaveBeforeZone = () => {
    if (pendingPositionRef.current === 'before') {
      setPosition(undefined);
    }
  };

  const handleDragEnterAfterZone = () => {
    setPosition('after');
    pendingPositionRef.current = 'after';
  };

  const handleDragLeaveAfterZone = () => {
    if (pendingPositionRef.current === 'after') {
      setPosition(undefined);
    }
  };

  const handleDrop = useCallback(() => {
    if (rootRef?.current) {
      const item = getItem();
      const currentDropKey = item != null ? (getKey(item as HTMLElement) as K) : undefined;
      const reference = currentDropKey;
      const itemKeys = getItemKeysArray(rootRef.current, itemSelector);
      let insertPos;
      const newArray = [...itemKeys];
      const dragIndex = newArray.indexOf(dragKey);
      if (reference != null) {
        const dropIndex = newArray.indexOf(reference);
        if (dragIndex != null && dropIndex != null && dragIndex != dropIndex) {
          if (!isRtl) {
            if (dragIndex < dropIndex) {
              insertPos = position === 'before' ? dropIndex - 1 : dropIndex;
            } else {
              insertPos = position === 'before' ? dropIndex : dropIndex + 1;
            }
          } else {
            if (dragIndex < dropIndex) {
              insertPos = position === 'before' ? dropIndex : dropIndex - 1;
            } else {
              insertPos = position === 'before' ? dropIndex + 1 : dropIndex;
            }
          }
        }
      }
      if (insertPos !== undefined && dragIndex !== insertPos) {
        const sourceData = itemKeys[dragIndex];
        newArray.splice(dragIndex, 1);
        newArray.splice(insertPos as number, 0, sourceData);
        onReorder?.({
          reorderedKeys: newArray as K[],
          itemKeys: [dragKey as K],
          // If the dragged items were dropped at the very beginning, the referenceKey will be null.
          referenceKey: insertPos === 0 ? null : (newArray[insertPos - 1] as K)
        });
        setPosition(undefined);
        setDragKey?.(undefined);
      }
    }
  }, [dragKey, position, getItem, isRtl, itemSelector, onReorder, rootRef, setDragKey]);

  if (isDisabled) {
    return {};
  }

  //change dragged item visual to appear disable
  const isDragged = itemKey != null && itemKey === dragKey;

  const dndItemClasses = classNames([styles.itemDnd, isDragged && styles.itemDragged]);

  const itemOverlayClassesBefore = classNames([
    itemStyles.itemOverlayFront,
    isVertical ? styles.itemOverlayVertical : styles.itemOverlay,
    position === 'before' && styles.itemOverlayBefore,
    position === 'before' && isRtl && styles.itemOverlayBeforeRtl
  ]);
  const itemOverlayClassesAfter = classNames([
    itemStyles.itemOverlayFront,
    itemStyles.itemOverlayRear,
    isVertical ? styles.itemOverlayVertical : styles.itemOverlay,
    position === 'after' && styles.itemOverlayAfter,
    position === 'after' && isRtl && styles.itemOverlayAfterRtl
  ]);

  return {
    rootProps: mergeProps({
      ref: ref,
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
      isDisabled: isDisabled,
      class: dndItemClasses
    }),
    isDragged: isDragged,
    overlayZoneBeforeProps: mergeProps({
      onDragEnter: handleDragEnterBeforeZone,
      onDragLeave: handleDragLeaveBeforeZone,
      class: itemOverlayClassesBefore
    }),
    overlayZoneAfterProps: mergeProps({
      onDragEnter: handleDragEnterAfterZone,
      onDragLeave: handleDragLeaveAfterZone,
      class: itemOverlayClassesAfter
    })
  } as const;
}
